import type { AxiosRequestConfig } from 'axios';

/**
 * Checks if the URL path from an Axios request config starts with any of the provided path prefixes
 * Handles both absolute URLs (with protocol/domain) and relative paths
 *
 * @param config - Axios request config containing the URL
 * @param pathPrefixes - Array of path prefixes to check against (e.g., ['/api/v1', '/api/v2'])
 * @returns true if the path starts with any of the provided prefixes, false otherwise
 *
 * @example
 * matchesPathPrefixes({ url: '/api/v1/users' }, ['/api/v1']) // returns true
 * matchesPathPrefixes({ url: 'https://example.com/api/v1/users' }, ['/api/v1']) // returns true
 * matchesPathPrefixes({ url: '/api/v2/users' }, ['/api/v1', '/api/v2']) // returns true
 * matchesPathPrefixes({ url: '/other/path' }, ['/api/v1']) // returns false
 */
export function matchesPathPrefixes(
  config: AxiosRequestConfig,
  pathPrefixes: string[],
): boolean {
  if (!config.url || pathPrefixes.length === 0) return false;

  // Extract path from URL (handles both absolute and relative URLs)
  let path: string;
  try {
    // If URL is absolute (starts with http:// or https://), parse it
    if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
      const url = new URL(config.url);
      path = url.pathname;
    } else {
      // If URL is relative, use it directly
      path = config.url;
    }
  } catch {
    // If URL parsing fails, fallback to using the URL string directly
    path = config.url;
  }

  // Check if path starts with any of the provided prefixes
  return pathPrefixes.some(prefix => path.startsWith(prefix));
}
